import { msg } from "@lingui/core/macro";
import { z } from "zod";
import { addressWithCoordinates } from "./addresses";
import {
  localizedPhoneNumberSchema,
  rawPhoneNumberSchema,
} from "./phoneNumbers";
import { dateSchema } from "./date-and-time";
import { siteSchema } from "./sites";
import type { MessageDescriptor } from "@lingui/core";

export const relativeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  phoneNumber: rawPhoneNumberSchema.nullish(),
  relation: z.string(),
  isLivingTogether: z.boolean(),
});
export type IRelative = z.infer<typeof relativeSchema>;

const patientIdSchema = z.uuid();
export type IPatientId = z.infer<typeof patientIdSchema>;

export const patientNameType = z.enum(["FullName", "GivenAndFamily"]);

export const givenAndFamilyNameInputSchema = z.object({
  givenName: z.string(),
  familyName: z.string(),
});
export type IGivenAndFamilyNameInput = z.infer<
  typeof givenAndFamilyNameInputSchema
>;
export const givenAndFamilyNameSchema = givenAndFamilyNameInputSchema.extend({
  type: z.literal(patientNameType.enum.GivenAndFamily),
});

const patientNameSchema = z.union([
  // Legacy format, needed during transition
  z.string(),
  // New format
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal(patientNameType.enum.FullName),
      value: z.string(),
    }),
    givenAndFamilyNameSchema,
  ]),
]);
export type IPatientName = z.infer<typeof patientNameSchema>;

export const getPatientFullName = (patientName: IPatientName) => {
  if (typeof patientName === "string") {
    return patientName;
  }
  if (patientName.type === patientNameType.enum.FullName) {
    return patientName.value;
  }
  return `${patientName.familyName}, ${patientName.givenName}`;
};

export const getPatientGivenName = (patientName: IPatientName) => {
  // Heuristic to try to get given name
  if (typeof patientName === "string") {
    return patientName.split(" ")[0] ?? "";
  }
  if (patientName.type === patientNameType.enum.FullName) {
    return patientName.value.split(" ")[0] ?? "";
  }
  // Proper way
  return patientName.givenName;
};

export const getPatientFamilyName = (patientName: IPatientName) => {
  // Heuristic to try to get family name
  if (typeof patientName === "string") {
    return patientName.split(" ").slice(1).join(" ");
  }
  if (patientName.type === patientNameType.enum.FullName) {
    return patientName.value.split(" ").slice(1).join(" ");
  }
  // Proper way
  return patientName.familyName;
};

const _relativeFormSchema = relativeSchema.extend({
  phoneNumber: localizedPhoneNumberSchema.nullish(),
});
export type IFormRelative = z.infer<typeof _relativeFormSchema>;

const employeeId = z.int();

export const patientStatusSchema = z.enum([
  "prospect",
  "admitted",
  "discharged",
  "declined",
  "deleted",
]);
export const patientStatusDictionary: Record<
  IPatientStatus,
  { singular: MessageDescriptor; plural: MessageDescriptor }
> = {
  prospect: { singular: msg`Under bedömning`, plural: msg`Under bedömning` },
  admitted: { singular: msg`Inskriven`, plural: msg`Inskrivna` },
  discharged: { singular: msg`Utskriven`, plural: msg`Utskrivna` },
  declined: { singular: msg`Fått avslag`, plural: msg`Fått avslag` },
  deleted: { singular: msg`Borttagen`, plural: msg`Borttagna` },
};
export type IPatientStatus = z.infer<typeof patientStatusSchema>;

export const activeStatuses = [
  patientStatusSchema.enum.prospect,
  patientStatusSchema.enum.admitted,
];

export const canDischargePatient = ({ status }: Pick<IPatient, "status">) =>
  status === patientStatusSchema.enum.admitted;

export const wardSchema = z.object({ displayName: z.string(), id: z.number() });

export const wardsSchema = z.array(wardSchema);

export type IWard = z.infer<typeof wardSchema>;

export const areaSchema = z.object({ displayName: z.string(), id: z.number() });

export const areasSchema = z.array(areaSchema);

export type IArea = z.infer<typeof areaSchema>;

export const teamSchema = z.object({
  displayName: z.string(),
  id: z.number(),
  displayColor: z.enum(["cyan", "brown", "yellow", "green", "violet", "pink"]),
});

export const teamsSchema = z
  .array(teamSchema)
  // Sites without teams have an entry with an empty displayName ("")
  .transform((teams) => teams.filter((team) => team.displayName));

export type ITeam = z.infer<typeof teamSchema>;

const economicUnitSchema = z.object({
  displayName: z.string(),
  id: z.number(),
});

export const economicUnitsSchema = z.array(economicUnitSchema);

export type IEconomicUnit = z.infer<typeof economicUnitSchema>;

export const careHubSchema = z.object({
  id: z.string(),
  latitude: z.number(),
  longitude: z.number(),
});

export const careHubsSchema = z.array(careHubSchema);

export type ICareHub = z.infer<typeof careHubSchema>;

export const patientTypes = [
  "HospitalAtHome",
  "AdvancedHomeCare",
  "External",
] as const;

export const patientTypeSchema = z.enum(patientTypes);
export const patientTypesSchema = z.array(patientTypeSchema);
export type IPatientType = z.infer<typeof patientTypeSchema>;

export const patientTypeDictionary: Record<IPatientType, MessageDescriptor> = {
  HospitalAtHome: msg`Sjukhus hemma`,
  AdvancedHomeCare: msg`ASIH`,
  External: msg`Särskilt uppdrag`,
};
export const deletedPatientSchema = z.object({
  id: patientIdSchema,
  status: z.literal(patientStatusSchema.enum.deleted),
});
export type IDeletedPatient = z.infer<typeof deletedPatientSchema>;

export const personalIdentityNumberTypeSchema = z.enum([
  "SwedishPersonalIdentityNumber",
  "SwedishTemporaryIdentityNumber",
  "SwissEMedicusNumber",
  "SwissPatientNumber",
  "LatvianPersonasKods",
]);

export type IPersonalIdentityNumberType = z.infer<
  typeof personalIdentityNumberTypeSchema
>;

export const formatPersonalIdentityNumber = ({
  personalIdentityNumber,
  personalIdentityNumberType,
}: {
  personalIdentityNumber: string;
  personalIdentityNumberType: IPersonalIdentityNumberType;
}) => {
  if (
    personalIdentityNumberType ===
    personalIdentityNumberTypeSchema.enum.SwedishPersonalIdentityNumber
  ) {
    return `${personalIdentityNumber.substring(0, 8)}-${personalIdentityNumber.substring(8, 12)}`;
  }
  return personalIdentityNumber;
};

// The value of the journey number's text input, which reports a field left empty as "".
// Decoding at the form boundary sends that as no journey number rather than an invalid one.
export const healthcareJourneyNumberInputSchema = z.codec(
  z.union([z.literal(""), z.string().length(8)]),
  z.string().length(8).nullable(),
  {
    decode: (value) => (value === "" ? null : value),
    encode: (healthcareJourneyNumber) => healthcareJourneyNumber ?? "",
  },
);

// Every field an existing patient carries, without the status literal that tells the
// variants below apart. A consumer that needs a patient whatever their status picks
// from here, rather than from the schema of one arbitrary status.
export const patientBaseSchema = z.object({
  id: patientIdSchema,
  address: addressWithCoordinates,
  healthcareJourneyNumber: z.string().length(8).nullish(),
  information: z.string().nullish(),
  name: patientNameSchema,
  personalIdentityNumber: z.string(),
  personalIdentityNumberType: personalIdentityNumberTypeSchema,
  phoneNumber: rawPhoneNumberSchema.nullish(),
  relatives: z.array(relativeSchema),
  safetyAlarmText: z.string().nullish(),
  registeredAt: dateSchema,
  ward: wardSchema,
  area: areaSchema.nullish(),
  team: teamSchema.transform((team) => (team.id === 1 ? null : team)).nullish(),
  site: siteSchema,
  economicUnit: economicUnitSchema.nullish(),
  patientType: patientTypeSchema,
  pet: z.string().nullish(),
  flags: z.string().nullish(),
  homeCare: z.boolean().nullish(),
  lifeCare: z.boolean().nullish(),
  externalCareEpisodeId: z.string().nullish(),
  plannedAdmissionDateTime: dateSchema.nullish(),
  plannedDischargeDateTime: dateSchema.nullish(),
  postDischargeProcessed: z.boolean().nullish(),
  primaryResponsible: employeeId.nullish(),
  referringUnit: z.string().nullish(),
  placeOfAssessment: z.string().nullish(),
  paymentExempt: z.boolean().nullish(),
  diagnosis: z
    .object({
      code: z.string(),
      title: z.string(),
    })
    .nullish(),
  careSummary: z.string().nullish(),
  language: z.string().nullish(),
  reasonForAdmission: z.string().nullish(),
});

export const prospectPatientSchema = patientBaseSchema.extend({
  status: z.literal(patientStatusSchema.enum.prospect),
});
export type IRegisteredPatient = z.infer<typeof prospectPatientSchema>;

export const admittedPatientSchema = prospectPatientSchema.extend({
  admittedAt: dateSchema,
  pausedAt: dateSchema.nullish(),
  status: z.literal(patientStatusSchema.enum.admitted),
});
export type IAdmittedPatient = z.infer<typeof admittedPatientSchema>;

export const pausedPatientSchema = admittedPatientSchema.extend({
  pausedAt: dateSchema,
});

export const declinedPatientSchema = prospectPatientSchema.extend({
  declinedAt: dateSchema,
  status: z.literal(patientStatusSchema.enum.declined),
});
export type IDeclinedPatient = z.infer<typeof declinedPatientSchema>;

export const dischargedPatientSchema = admittedPatientSchema.extend({
  dischargedAt: dateSchema,
  status: z.literal(patientStatusSchema.enum.discharged),
});
export type IDischargedPatient = z.infer<typeof dischargedPatientSchema>;

// Standardized omitters
const listPatient = {
  economicUnit: true,
  healthcareJourneyNumber: true,
  relatives: true,
  safetyAlarmText: true,
  homeCare: true,
  lifeCare: true,
} as const;

const _patientSchema = z.discriminatedUnion("status", [
  prospectPatientSchema,
  admittedPatientSchema,
  declinedPatientSchema,
  dischargedPatientSchema,
  deletedPatientSchema,
]);
export type IPatient = z.infer<typeof _patientSchema>;

export const existingPatientSchema = z.discriminatedUnion("status", [
  prospectPatientSchema,
  admittedPatientSchema,
  declinedPatientSchema,
  dischargedPatientSchema,
]);
export type IExistingPatient = z.infer<typeof existingPatientSchema>;

/** When the patient got their current status */
export const getStatusTimestamp = (patient: IExistingPatient) => {
  switch (patient.status) {
    case patientStatusSchema.enum.prospect:
      return patient.registeredAt;
    case patientStatusSchema.enum.admitted:
      return patient.admittedAt;
    case patientStatusSchema.enum.discharged:
      return patient.dischargedAt;
    case patientStatusSchema.enum.declined:
      return patient.declinedAt;
    default:
      return null;
  }
};

export const listProspectPatientSchema =
  prospectPatientSchema.omit(listPatient);
export const listAdmittedPatientSchema =
  admittedPatientSchema.omit(listPatient);
export const listDischargedPatientSchema =
  dischargedPatientSchema.omit(listPatient);

export const listPatientSchema = z.discriminatedUnion("status", [
  prospectPatientSchema.omit(listPatient),
  admittedPatientSchema.omit(listPatient),
  declinedPatientSchema.omit(listPatient),
  dischargedPatientSchema.omit(listPatient),
  deletedPatientSchema,
]);

export const listExistingPatientSchema = z.discriminatedUnion("status", [
  prospectPatientSchema.omit(listPatient),
  admittedPatientSchema.omit(listPatient),
  declinedPatientSchema.omit(listPatient),
  dischargedPatientSchema.omit(listPatient),
]);

const prospectPatientWithHomeVisitSchema = prospectPatientSchema
  .omit(listPatient)
  .extend({ hasHomeVisit: z.boolean() });

const admittedPatientWithHomeVisitSchema = admittedPatientSchema
  .omit(listPatient)
  .extend({ hasHomeVisit: z.boolean() });

const declinedPatientWithHomeVisitSchema = declinedPatientSchema
  .omit(listPatient)
  .extend({ hasHomeVisit: z.boolean() });

const dischargedPatientWithHomeVisitSchema = dischargedPatientSchema
  .omit(listPatient)
  .extend({ hasHomeVisit: z.boolean() });

export const listExistingPatientWithHomeVisitFlagSchema = z.discriminatedUnion(
  "status",
  [
    prospectPatientWithHomeVisitSchema,
    admittedPatientWithHomeVisitSchema,
    declinedPatientWithHomeVisitSchema,
    dischargedPatientWithHomeVisitSchema,
  ],
);

export const careEpisodeSchema = z.object({
  careEpisodeId: z.uuid(),
  address: addressWithCoordinates,
  admittedAt: dateSchema.nullish(),
  area: areaSchema.nullish(),
  team: teamSchema.transform((team) => (team.id === 1 ? null : team)).nullish(),
  economicUnit: economicUnitSchema.nullish(),
  declinedAt: dateSchema.nullish(),
  diagnosis: z
    .object({
      code: z.string(),
      title: z.string(),
    })
    .nullish(),
  dischargedAt: dateSchema.nullish(),
  healthcareJourneyNumber: z.string().length(8).nullish(),
  homeCare: z.boolean().nullish(),
  information: z.string().nullish(),
  lifeCare: z.boolean().nullish(),
  name: patientNameSchema,
  patientType: patientTypeSchema,
  personalIdentityNumber: z.string().nullish(),
  personalIdentityNumberType: personalIdentityNumberTypeSchema,
  pet: z.string().nullish(),
  flags: z.string().nullish(),
  phoneNumber: rawPhoneNumberSchema.nullish(),
  registeredAt: dateSchema,
  relatives: z.array(relativeSchema),
  safetyAlarmText: z.string().nullish(),
  site: siteSchema,
  status: patientStatusSchema,
  ward: wardSchema,
  externalCareEpisodeId: z.string().nullish(),
  plannedAdmissionDateTime: dateSchema.nullish(),
  postDischargeProcessed: z.boolean().nullish(),
  primaryResponsible: employeeId.nullish(),
  referringUnit: z.string().nullish(),
  placeOfAssessment: z.string().nullish(),
  paymentExempt: z.boolean().nullish(),
  language: z.string().nullish(),
  reasonForAdmission: z.string().nullish(),
  careSummary: z.string().nullish(),
});
export type ICareEpisode = z.infer<typeof careEpisodeSchema>;
